import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { EurecaLogoMark } from '@eureca/ui/icons';
import type { PageModel } from 'survey-core';
import { SurveyCreatorWrapper } from '@/features/forms/form-edit/SurveyCreatorWrapper';
import {
  AlignmentFormEditPage as AlignmentFormEditPageView,
  NO_VISIBLE_PAGE_MESSAGE,
  type AlignmentFormDeletePageRequest,
  type AlignmentFormEditView,
} from './AlignmentFormEditPage.component';
import { alignmentFormQueryOptions } from './alignment-form-query';
import { AlignmentFormMutationError, usePutAlignmentForm } from './alignment-form-mutations';
import { hasVisiblePage } from './alignment-restrictions';
import { explainViolations } from './alignment-violations';
import type { AlignmentBinding } from './bind-alignment-restrictions';

export const ALIGNMENT_FORM_EDIT_ROUTE_ID =
  '/_authenticated/programas/$programId/alinhamento/editar';

const SAVE_SUCCESS_MESSAGE = 'Formulário editado com sucesso';
const SAVE_ERROR_MESSAGE = 'Algo deu errado. Tente novamente';

interface DeleteRequest extends AlignmentFormDeletePageRequest {
  confirm: () => void;
}

/**
 * DEV-269 — container da edição do formulário de alinhamento (spec 138).
 *
 * - Lê `GET /programs/:id/alignment-form` (template se nunca editado — AC-31).
 * - Monta o `SurveyCreatorWrapper` em modo restrito com as `restrictions` da API.
 * - "Salvar": recusa inline sem página visível (AC-35); `PUT`; sucesso → volta
 *   ao programa + Sonner "Formulário editado com sucesso"; falha → permanece
 *   com tudo preenchido + Sonner "Algo deu errado. Tente novamente" (AC-48).
 * - "Voltar" com alteração pendente avisa antes (useBlocker — lição EU-761:
 *   ler `dirty` por ref dentro de `shouldBlockFn`).
 */
export function AlignmentFormEditPage() {
  const { programId } = useParams({ from: ALIGNMENT_FORM_EDIT_ROUTE_ID });
  const navigate = useNavigate();

  const formQuery = useQuery(alignmentFormQueryOptions(programId));
  const putForm = usePutAlignmentForm(programId);

  const definitionRef = useRef<unknown>(null);
  const dirtyRef = useRef(false);
  const savedRef = useRef(false);
  // Guarda SÍNCRONA do Salvar: `isPending` do React Query só muda no próximo render,
  // então um clique duplo real (dois cliques em ~6 ms) passava pelos dois — QA 09/10.
  const savingRef = useRef(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [deleteRequest, setDeleteRequest] = useState<DeleteRequest | null>(null);

  // Alteração pendente = editou, não gravou e não está gravando agora.
  const hasPendingChange = useCallback(
    () => dirtyRef.current && !savedRef.current && !savingRef.current,
    []
  );
  // SC-11 — o mesmo "Sair sem salvar?" para o botão "Voltar" da tela, o voltar/avançar
  // do navegador (popstate: o blocker do TanStack segura a navegação até a resposta) e
  // qualquer link do app. Recarregar ou fechar a aba (e um voltar que sai do app) não
  // passa pelo roteador: aí só o aviso nativo do navegador (beforeunload) protege —
  // QA 09/10 (EDT-028): o voltar do navegador saía sem aviso.
  const blocker = useBlocker({
    shouldBlockFn: hasPendingChange,
    enableBeforeUnload: hasPendingChange,
    withResolver: true,
  });

  // Semeia a definição em memória com a resposta do GET (uma vez).
  useEffect(() => {
    if (formQuery.data && definitionRef.current === null) {
      definitionRef.current = formQuery.data.definition;
    }
  }, [formQuery.data]);

  const handleChange = useCallback((definition: unknown) => {
    definitionRef.current = definition;
    dirtyRef.current = true;
  }, []);

  const restriction = useMemo<AlignmentBinding | undefined>(() => {
    const restrictions = formQuery.data?.restrictions;
    if (!restrictions) return undefined;
    return {
      restrictions,
      onRejected: (message) => toast.error(message),
      onConfirmDeletePage: (page: PageModel, deletePage) =>
        setDeleteRequest({
          pageTitle: page.title || page.name,
          questionCount: page.questions.length,
          confirm: deletePage,
        }),
    };
  }, [formQuery.data?.restrictions]);

  function goBackToProgram() {
    void navigate({ to: '/programas/$programId', params: { programId } });
  }

  async function handleSave() {
    // Clique duplo real (dois cliques em ~6 ms) = uma gravação e um Sonner (EDT-029 / AC-48).
    if (savingRef.current || putForm.isPending) return;
    const definition = definitionRef.current ?? formQuery.data?.definition;
    if (!definition) return;
    if (!hasVisiblePage(definition as { pages?: { visible?: boolean }[] })) {
      setInlineError(NO_VISIBLE_PAGE_MESSAGE);
      return;
    }
    savingRef.current = true;
    setInlineError(null);
    try {
      // Sem alteração o PUT vai do mesmo jeito (idempotente): a pessoa sempre recebe
      // o retorno "Formulário editado com sucesso" — QA 09/10 (EDT-031 / AC-48).
      await putForm.mutateAsync({ definition: definition as Record<string, unknown> });
      savedRef.current = true;
      dirtyRef.current = false;
      toast(SAVE_SUCCESS_MESSAGE, { action: { label: 'Fechar', onClick: () => undefined } });
      goBackToProgram();
    } catch (err) {
      if (err instanceof AlignmentFormMutationError) {
        if (err.hasViolation('no_visible_page')) {
          setInlineError(NO_VISIBLE_PAGE_MESSAGE);
          return;
        }
        // 422 com violações explicáveis (ex.: regra de Lógica que esconde um campo
        // protegido): nomeia o campo no painel em vez do Sonner genérico (SC-14 / AC-41).
        const explained = explainViolations(err.violations, definition);
        if (explained) {
          setInlineError(explained);
          return;
        }
      }
      toast.error(SAVE_ERROR_MESSAGE);
    } finally {
      savingRef.current = false;
    }
  }

  const view: AlignmentFormEditView = formQuery.isError
    ? 'error'
    : formQuery.data
      ? 'ready'
      : 'loading';

  return (
    <AlignmentFormEditPageView
      view={view}
      // AC-28 / Figma 16093:41525 — a marca "eu" da Eureca num quadrado verde
      // (o formulário é da Eureca; a empresa aparece na tela do programa).
      brand={
        <div
          className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary"
          data-testid="alignment-form-brand"
        >
          <EurecaLogoMark aria-label="Eureca" className="h-3 w-auto text-primary-foreground" />
        </div>
      }
      builder={
        formQuery.data ? (
          <SurveyCreatorWrapper
            formId={programId}
            initialDefinition={formQuery.data.definition}
            onChange={handleChange}
            restriction={restriction}
          />
        ) : undefined
      }
      isSaving={putForm.isPending}
      inlineError={inlineError}
      onBack={goBackToProgram}
      onSave={() => void handleSave()}
      onRetry={() => void formQuery.refetch()}
      leaveDialogOpen={blocker.status === 'blocked'}
      onLeaveDialogOpenChange={(open) => {
        if (!open && blocker.status === 'blocked') blocker.reset();
      }}
      onConfirmLeave={() => {
        if (blocker.status === 'blocked') blocker.proceed();
      }}
      deletePageRequest={deleteRequest}
      onDeletePageDialogOpenChange={(open) => {
        if (!open) setDeleteRequest(null);
      }}
      onConfirmDeletePage={() => {
        deleteRequest?.confirm();
        setDeleteRequest(null);
      }}
    />
  );
}
